import clsx from 'clsx';
import React from 'react';
import { useState } from 'react';
import { DatePicker } from '@material-ui/pickers';
import moment from 'moment';

interface Props {
  dateRange: {
    start: Date;
    end: Date;
  };
  onDateChange(range: { start: any; end: any }): void;
  quickRange?: boolean;
  showQuickDays?: boolean;
  showQuickToDates?: boolean;
  disabled?: boolean;
}

export const DateRangeBox = ({
  dateRange,
  disabled = false,
  onDateChange,
  quickRange,
  showQuickDays,
  showQuickToDates,
}: Props) => {
  const [currentFilter, setCurrentFilter] = useState(0);
  const classes = useStyles();

  const handleQuickRange = (event: any) => {
    const daysFromButtonId = parseInt(event.currentTarget.id);
    if (daysFromButtonId === currentFilter) {
      setCurrentFilter(0);
      onDateChange({
        start: moment(),
        end: moment(),
      });
      return;
    }
    switch (daysFromButtonId) {
      case 1000:
        onDateChange({ start: moment().startOf('isoWeek'), end: moment() });
        break;
      case 2000:
        onDateChange({ start: moment().startOf('month'), end: moment() });
        break;
      default:
        onDateChange({
          start: moment().subtract(daysFromButtonId, 'days'),
          end: dateRange.end,
        });
        break;
    }
    setCurrentFilter(daysFromButtonId);
  };

  return (
    <div className="flex flex-col items-center gap-4 p-4 w-full">
      <div className="flex flex-col sm:flex-row md:flex-col items-center justify-center gap-4 w-full">
        <DatePicker
          DialogProps={{
            className: '!z-[99999]',
          }}
          label="Start Date"
          value={dateRange.start}
          onChange={(date: any) =>
            onDateChange({ start: date, end: dateRange.end })
          }
          animateYearScrolling
          autoOk
          disableFuture
          maxDate={dateRange.end}
          inputVariant="filled"
          format="MMMM DD YYYY"
          onAccept={() => setCurrentFilter(0)}
          showTodayButton
          disabled={disabled}
        />
        <DatePicker
          DialogProps={{
            className: '!z-[99999]',
          }}
          label="End Date"
          value={dateRange.end}
          onChange={(date: any) =>
            onDateChange({ start: dateRange.start, end: date })
          }
          animateYearScrolling
          autoOk
          disableFuture
          minDate={dateRange.start}
          minDateMessage="End date cannot be before start date"
          inputVariant="filled"
          format="MMMM DD YYYY"
          onAccept={() => setCurrentFilter(0)}
          showTodayButton
          disabled={disabled}
        />
      </div>
      {quickRange ? (
        <div
          className={clsx('flex flex-col sm:flex-row md:flex-col gap-2 w-fit', {
            'pointer-events-none opacity-30': disabled,
          })}
        >
          {showQuickDays ? (
            <div className={classes.buttonGroup}>
              <button
                id="7"
                className={clsx(
                  classes.button,
                  'border-r border-border-light dark:border-border-dark',
                  {
                    [classes.selectedButton]: currentFilter === 7,
                  }
                )}
                disabled={disabled}
                onClick={handleQuickRange}
              >
                7 Days
              </button>
              <button
                id="30"
                className={clsx(classes.button, {
                  [classes.selectedButton]: currentFilter === 30,
                })}
                disabled={disabled}
                onClick={handleQuickRange}
              >
                30 Days
              </button>
            </div>
          ) : null}
          {showQuickToDates ? (
            <div className={classes.buttonGroup}>
              <button
                id="1000"
                className={clsx(
                  classes.button,
                  'border-r border-border-light dark:border-border-dark',
                  {
                    [classes.selectedButton]: currentFilter === 1000,
                  }
                )}
                disabled={disabled}
                onClick={handleQuickRange}
              >
                WTD
              </button>
              <button
                id="2000"
                className={clsx(classes.button, {
                  [classes.selectedButton]: currentFilter === 2000,
                })}
                disabled={disabled}
                onClick={handleQuickRange}
              >
                MTD
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

const useStyles = () => ({
  button:
    'cursor-pointer text-xs uppercase p-2 hover:bg-black/10 hover:dark:bg-white/10 w-[100px]',
  buttonGroup:
    'flex justify-center whitespace-nowrap rounded border border-border-light dark:border-border-dark overflow-hidden w-full',
  selectedButton: 'bg-primary text-white hover:!bg-primary',
});
